/**
 * Mission Sub-KPI Configuration Wizard
 *
 * 4-step stepped wizard for Add/Edit Mission sub-KPI flow:
 *   Step 1: Type selection
 *   Step 2: Display name + type-specific configuration
 *   Step 3: Reporting cadence & role assignment
 *   Step 4: Review & save
 *
 * DEV-882 / Skip — name moved from Step 1 to Step 2 so the user picks a
 * type first, then names + configures it together. Earlier layout had two
 * Display Name fields (Step 1 and per-type schema in Step 2); now there
 * is exactly one, and it's the field that drives instance.display_name.
 *
 * Add mode: Linear flow (Next/Back, steps not clickable)
 * Edit mode: All steps are jump points, "Show all" toggle flattens to single page
 *
 * Uses the mission type registry to drive field rendering dynamically.
 * Persists as a SubKpiInstance with mission_type_key and mission_type_config fields.
 */

import React, { useReducer, useMemo, useState, useEffect } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { SubKpiInstance } from '../../Data/ProjectConfig/projectKpiConfigOverrides';
import { listMissionSubKpiTypes, getMissionSubKpiType } from '../../kpi-engine/missionTypes/registry';
import type { MissionSubKpiType } from '../../kpi-engine/missionTypes/types';
import { ReportingCadenceWidget, DEFAULT_CADENCE_VALUE } from '../ReportingCadenceWidget/ReportingCadenceWidget';
import type { CadenceValue } from '../ReportingCadenceWidget/ReportingCadenceWidget';
// DEV-816 Variant E redesign — mode picker as cards, live preview indicator.
import { ModeCardList, type ModeOption } from '../MissionKpi/ModeCardRow';
import { LiveScorePreview } from '../MissionKpi/LiveScorePreview';
import styles from './MissionKpiWizard.module.css';

// ── Types ──────────────────────────────────────────────────────

type WizardMode = 'add' | 'edit';

export interface MissionKpiWizardProps {
  mode: WizardMode;
  existingInstance?: SubKpiInstance;
  projectGuid: string;
  onSave: (instance: SubKpiInstance) => void;
  onCancel: () => void;
}

interface WizardState {
  displayName: string;
  missionTypeKey: string;
  typeConfig: Record<string, unknown>;
  cadenceValue: CadenceValue;
  assignedRoleGuid: string | null;
  permissibleDaysLate: number;
  currentStep: number;
  showAll: boolean;
  errors: Record<string, string>;
}

type WizardAction =
  | { type: 'SET_DISPLAY_NAME'; value: string }
  | { type: 'SET_MISSION_TYPE'; value: string; typeInstance: MissionSubKpiType }
  | { type: 'SET_TYPE_CONFIG'; value: Record<string, unknown> }
  | { type: 'UPDATE_TYPE_CONFIG_FIELD'; key: string; value: unknown }
  | { type: 'CHANGE_COLOR_MODE'; value: string; schema: any[] }
  | { type: 'SET_CADENCE'; value: CadenceValue }
  | { type: 'SET_ASSIGNED_ROLE'; value: string | null }
  | { type: 'SET_PERMISSIBLE_DAYS_LATE'; value: number }
  | { type: 'SET_STEP'; value: number }
  | { type: 'TOGGLE_SHOW_ALL' }
  | { type: 'SET_ERRORS'; value: Record<string, string> }
  | { type: 'CLEAR_ERRORS' };

// ── Initial State & Reducer ────────────────────────────────────

function createInitialState(mode: WizardMode, existing?: SubKpiInstance): WizardState {
  if (mode === 'edit' && existing) {
    // Load from existing instance.
    //
    // mission_type_key / mission_type_config are the canonical storage for new
    // wizard-created instances, BUT they are not yet persisted by the API
    // (DEV-814 follow-up — backend migration pending). Until that ships, every
    // edit-mode load round-trips through the legacy fields. We must reverse-
    // bridge legacy fields back into typeConfig so the form populates with
    // what the user actually saved.
    //
    // Resolution order:
    //   1. mission_type_key from instance, OR inferred from value_type+direction
    //   2. mission_type_config from instance — IF present and non-empty
    //   3. Otherwise, reconstruct typeConfig from legacy fields
    const inferredTypeKey =
      (existing as any).mission_type_key ||
      inferTypeKeyFromLegacy(existing) ||
      'binary';

    const persistedConfig = (existing as any).mission_type_config;
    const typeConfig =
      persistedConfig && Object.keys(persistedConfig).length > 0
        ? persistedConfig
        : reverseBridgeFromLegacy(inferredTypeKey, existing);

    // HSE rows have a locked display name derived from hse_category —
    // Health / Safety / Environment. Override whatever's currently in
    // existing.display_name with the canonical value so the wizard always
    // shows the right name. The input is also rendered as readOnly below.
    const canonicalHseName = (() => {
      if (inferredTypeKey !== 'incident_count') return null;
      const cat = String((typeConfig as any)?.hse_category ?? '').toLowerCase();
      if (cat === 'health') return 'Health';
      if (cat === 'safety') return 'Safety';
      if (cat === 'environment') return 'Environment';
      return null;
    })();

    return {
      displayName: canonicalHseName ?? (existing.display_name || ''),
      missionTypeKey: inferredTypeKey,
      typeConfig: canonicalHseName
        ? { ...typeConfig, display_name: canonicalHseName }
        : typeConfig,
      cadenceValue: {
        cadence: (existing.cadence as any) || 'weekly',
        interval: 1,
        weekdays: [false, false, false, false, false, true, false],
        monthlyMode: 'day',
        monthlyDayOfMonth: 1,
        monthlyWeekday: 1,
      },
      // Load reporting fields from the existing instance (DEV-814 H1).
      // Was hardcoded null/2 — meant edit mode silently reset role + cadence
      // guard on every save.
      assignedRoleGuid: ((existing as any).assigned_role_guid as string | undefined) ?? null,
      permissibleDaysLate: typeof (existing as any).permissible_days_late === 'number'
        ? (existing as any).permissible_days_late
        : 2,
      currentStep: 2, // Land on Configuration in edit mode
      showAll: false,
      errors: {},
    };
  }

  // Add mode: start fresh
  const defaultType = listMissionSubKpiTypes()[0]; // First type in registry
  return {
    displayName: '',
    missionTypeKey: defaultType?.type_key || 'binary',
    typeConfig: {},
    cadenceValue: DEFAULT_CADENCE_VALUE,
    assignedRoleGuid: null,
    permissibleDaysLate: 2,
    currentStep: 1,
    showAll: false,
    errors: {},
  };
}

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_DISPLAY_NAME':
      return { ...state, displayName: action.value };
    case 'SET_MISSION_TYPE':
      return {
        ...state,
        missionTypeKey: action.value,
        // Reset config when type changes, but preserve the user's chosen
        // display name (DEV-882 / Skip): the name lives at the wizard level
        // and is mirrored into typeConfig.display_name. Wiping typeConfig
        // entirely would erase the name the user just typed if they then
        // change their mind about the type.
        typeConfig: state.displayName ? { display_name: state.displayName } : {},
      };
    case 'SET_TYPE_CONFIG':
      return { ...state, typeConfig: action.value };
    case 'UPDATE_TYPE_CONFIG_FIELD':
      return {
        ...state,
        typeConfig: { ...state.typeConfig, [action.key]: action.value }
      };
    case 'CHANGE_COLOR_MODE': {
      // DEV-816 / Skip: when user picks a different color mode, reset
      // the threshold fields for the NEW mode to their schema defaults
      // so we don't carry stale values across modes (which would trigger
      // false-positive validation errors). The old mode's threshold
      // values are intentionally cleared — switching modes commits to
      // the new mode's defaults, not a hybrid.
      const next: Record<string, unknown> = {
        ...state.typeConfig,
        color_mode: action.value,
      };
      // Whitelist of threshold field keys per mode. Each mode's threshold
      // fields are reset to their schema-declared default_value when the
      // mode becomes active; the other modes' fields are left as-is
      // (they're hidden by visible_when and don't affect runtime scoring).
      const KEYS_BY_MODE: Record<string, string[]> = {
        score:    ['green_threshold', 'yellow_threshold'],
        deadline: ['warning_days', 'critical_days'],
        pace:     ['pace_yellow_units_behind', 'pace_red_units_behind'],
      };
      const targetKeys = KEYS_BY_MODE[action.value] ?? [];
      for (const key of targetKeys) {
        const fieldDef = action.schema.find((f: any) => f.key === key);
        if (fieldDef && fieldDef.default_value !== undefined) {
          next[key] = fieldDef.default_value;
        }
      }
      return { ...state, typeConfig: next };
    }
    case 'SET_CADENCE':
      return { ...state, cadenceValue: action.value };
    case 'SET_ASSIGNED_ROLE':
      return { ...state, assignedRoleGuid: action.value };
    case 'SET_PERMISSIBLE_DAYS_LATE':
      return { ...state, permissibleDaysLate: action.value };
    case 'SET_STEP':
      return { ...state, currentStep: action.value };
    case 'TOGGLE_SHOW_ALL':
      return { ...state, showAll: !state.showAll };
    case 'SET_ERRORS':
      return { ...state, errors: action.value };
    case 'CLEAR_ERRORS':
      return { ...state, errors: {} };
    default:
      return state;
  }
}

// ── Component ──────────────────────────────────────────────────

export function MissionKpiWizard({
  mode,
  existingInstance,
  projectGuid,
  onSave,
  onCancel,
}: MissionKpiWizardProps) {
  const { t } = useAppTranslation('projectConfig');
  const [state, dispatch] = useReducer(wizardReducer, mode, (m) => createInitialState(m, existingInstance));

  // Get the currently selected type
  const selectedType = useMemo(() => {
    return getMissionSubKpiType(state.missionTypeKey);
  }, [state.missionTypeKey]);

  // List all available types for the picker.
  //
  // DEV-822 / Skip: HSE Incident Tracker (incident_count) is excluded from
  // the create-flow picker. HSE cards are always-on (Model A) — every
  // project gets exactly three (Health/Safety/Environment), provisioned
  // automatically. Letting users add a fourth would create a duplicate
  // and break the 1-per-category invariant. They remain in the registry
  // so existing rows can still be EDITED via this wizard; they just
  // can't be added new.
  const availableTypes = useMemo(() => {
    return listMissionSubKpiTypes().filter(
      (t) => t.type_key !== 'incident_count',
    );
  }, []);

  // Real-time cross-field validation (DEV-816 / Skip).
  //
  // The wizard's validateStep only fires on Next/Save, so cross-field errors
  // (yellow > red, deadline >= tracking_start, Pace mode requires both
  // dates, target > baseline, etc.) used to be invisible until commit. That
  // gave Skip the impression nothing was being validated. We now run the
  // type's validate_config on every typeConfig change and surface its output
  // immediately — inline at the named field for { field, message } returns,
  // or as a step-level banner for plain string returns.
  //
  // This is purely for UX feedback; validateStep still runs the same checks
  // at save-time so nothing slips through if the user manages to dispatch a
  // save without seeing the error.
  const [crossFieldError, setCrossFieldError] = useState<
    { field: string; message: string } | null
  >(null);
  useEffect(() => {
    if (!selectedType?.validate_config) {
      setCrossFieldError(null);
      return;
    }
    // DEV-816 bug fix (Skip's screenshot): the per-type validateConfig
    // functions check `config.display_name`, but the wizard stores the
    // display name as `state.displayName` (Step 1 field), separate from
    // typeConfig. Without merging, validators always see display_name as
    // missing and emit "Display name is required" the moment the user
    // arrives on Step 2 — even though they typed the name on Step 1.
    // Merge displayName in so validators see the same shape they'd see
    // in template-authoring or import contexts.
    const configForValidation = {
      ...state.typeConfig,
      display_name: state.displayName,
    };
    const result = selectedType.validate_config(configForValidation as any);
    if (!result) {
      setCrossFieldError(null);
    } else if (typeof result === 'string') {
      // Legacy plain-string error — surface as a step-level banner via the
      // pseudo-field `_type`. Newer validators should return { field, message }
      // so the error lands at the offending input.
      setCrossFieldError({ field: '_type', message: result });
    } else {
      setCrossFieldError(result);
    }
    // displayName is part of the merged config we validate; include it
    // in deps so the validator re-runs when the user types/clears it on
    // Step 1, otherwise a stale "Display name is required" can stick.
  }, [state.typeConfig, state.displayName, selectedType]);

  // Merge live cross-field errors into the per-field error map for render.
  // The save-time errors (state.errors) take precedence so a stale "this is
  // required" doesn't get overwritten by a fresh cross-field error.
  const displayErrors = useMemo(() => {
    if (!crossFieldError) return state.errors;
    const merged = { ...state.errors };
    // Only add the cross-field error if no save-time error already exists
    // for that field. This keeps "required" + "out of range" errors visible
    // when both apply.
    if (!merged[crossFieldError.field]) {
      merged[crossFieldError.field] = crossFieldError.message;
    }
    return merged;
  }, [state.errors, crossFieldError]);

  const handleStepClick = (stepNum: number) => {
    if (mode === 'edit') {
      dispatch({ type: 'SET_STEP', value: stepNum });
    }
  };

  // DEV-822 / Skip: HSE-typed sub-KPIs in edit mode collapse the wizard
  // to Configuration (2) + Reporting (3). Step 1 (Type) is irrelevant
  // (type is fixed); Step 4 (Review) is overkill for low-stakes edits.
  // Last step in HSE edit is 3, where Next becomes Save.
  const isHseEdit = mode === 'edit' && state.missionTypeKey === 'incident_count';
  const lastStep = isHseEdit ? 3 : 4;
  const firstStep = isHseEdit ? 2 : 1;

  const handleNext = () => {
    if (state.currentStep < lastStep) {
      // Validate current step before advancing
      const stepErrors = validateStep(state.currentStep, state, selectedType);
      if (Object.keys(stepErrors).length > 0) {
        dispatch({ type: 'SET_ERRORS', value: stepErrors });
        return;
      }
      dispatch({ type: 'CLEAR_ERRORS' });
      dispatch({ type: 'SET_STEP', value: state.currentStep + 1 });
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    if (state.currentStep > firstStep) {
      dispatch({ type: 'CLEAR_ERRORS' });
      dispatch({ type: 'SET_STEP', value: state.currentStep - 1 });
    }
  };

  const handleSave = () => {
    // Validate all steps
    const allErrors: Record<string, string> = {};
    for (let step = 1; step <= 4; step++) {
      Object.assign(allErrors, validateStep(step, state, selectedType));
    }

    if (Object.keys(allErrors).length > 0) {
      dispatch({ type: 'SET_ERRORS', value: allErrors });
      return;
    }

    // Build SubKpiInstance.
    //
    // The engine (mission.ts → computeMissionKpiSignal) reads from LEGACY
    // fields — value_type, direction, target_goal, target_baseline,
    // deadline_date, warning_days, critical_days, etc. — to score the KPI.
    //
    // The new mission-types architecture (registry, binary.ts, count.ts,
    // reduction.ts) is built but not yet wired into the engine — that is
    // DEV-814 Phase 4 work. Until then, the wizard must write BOTH the new
    // mission_type_config AND the legacy fields the engine still reads from.
    //
    // bridgeToLegacyFields() does this translation per type. When the engine
    // migrates to the new types, this bridge can be deleted.
    const legacyFields = bridgeToLegacyFields(
      state.missionTypeKey,
      state.typeConfig,
      state.cadenceValue.cadence,
    );

    // HSE names are platform-fixed (Health / Safety / Environment), derived
    // from mission_type_config.hse_category. Even if state.displayName has
    // drifted from the canonical value (legacy data, hand-edited JSON, etc.)
    // the save MUST persist the canonical name. Belt-and-braces — the input
    // is also readOnly in the UI.
    const canonicalHseName = (() => {
      if (state.missionTypeKey !== 'incident_count') return null;
      const cat = String((state.typeConfig as any)?.hse_category ?? '').toLowerCase();
      if (cat === 'health') return 'Health';
      if (cat === 'safety') return 'Safety';
      if (cat === 'environment') return 'Environment';
      return null;
    })();

    const instance: SubKpiInstance = {
      // Existing instance fields preserved on edit (e.g., weight, is_mandatory)
      ...(existingInstance || {}),
      // Identity
      instance_guid: existingInstance?.instance_guid || generateGuid(),
      catalog_kpi_guid: 'kpi_mission_outcome_v1',
      display_name: canonicalHseName
        ?? state.displayName
        ?? `Mission KPI (${state.missionTypeKey})`,
      display_mode: (existingInstance?.display_mode as any) || 'full',
      is_mandatory: existingInstance?.is_mandatory ?? false,
      // Cadence stored as a top-level string for engine + reliability scoring.
      // DEV-651 / 2026-05-11: event-driven Mission types (binary, milestone_set,
      // incident_count) ALWAYS persist as cadence='once'. The wizard hides the
      // cadence widget for these types in Step 3, but we still force the value
      // here so edits on existing rows with legacy non-'once' cadence (carried
      // over from before the wizard had this guard) get corrected on next save.
      // The selector at buildExpectedInstancesForProject skips cadence='once'
      // from expected_instances, so these rows don't appear in the Data
      // Reliability matrix and don't get scored for cadenced timeliness.
      cadence: (
        state.missionTypeKey === 'binary'
        || state.missionTypeKey === 'milestone_set'
        || state.missionTypeKey === 'incident_count'
      )
        ? 'once'
        : (state.cadenceValue.cadence as any),
      // Reporting fields captured by Step 3 — previously discarded (DEV-814 H1).
      // Backend persistence requires migration 121.
      assigned_role_guid: state.assignedRoleGuid || undefined,
      permissible_days_late: Number.isFinite(state.permissibleDaysLate)
        ? state.permissibleDaysLate
        : undefined,
      // Legacy fields the engine reads — translated from the type config
      ...legacyFields,
    } as any;

    // New mission-types fields stored alongside legacy fields for forward
    // compatibility. The wizard reads from these on edit; the engine ignores
    // them today (will read in DEV-814 Phase 4).
    (instance as any).mission_type_key = state.missionTypeKey;
    // For HSE rows, force the canonical display_name into the JSONB config
    // too so the stored config agrees with the top-level display_name.
    (instance as any).mission_type_config = canonicalHseName
      ? { ...(state.typeConfig as any), display_name: canonicalHseName }
      : state.typeConfig;

    // (Removed 2026-05-11 per Skip — diagnostic console.debug at save
    // time. Re-add behind a debugMode gate if a regression needs the trace.)

    onSave(instance);
  };

  /**
   * Translate the new mission-types config into the LEGACY SubKpiInstance
   * fields that the engine (mission.ts → computeMissionKpiSignal) reads
   * for scoring. When the engine migrates to the new types architecture
   * (DEV-814 Phase 4), this bridge can be deleted.
   *
   * Field mapping per type:
   *   binary    → value_type='binary', deadline_date, warning_days, critical_days
   *   count     → value_type='numeric', direction='increase_is_good', target_goal,
   *               aggregation='cumulative', deadline_date, warning_days, critical_days
   *   reduction → value_type='numeric', direction='decrease_is_good', target_goal,
   *               target_baseline, deadline_date, warning_days, critical_days
   */
  function bridgeToLegacyFields(
    typeKey: string,
    config: Record<string, unknown>,
    cadence: string,
  ): Partial<SubKpiInstance> {
    const c = config as any;

    if (typeKey === 'binary') {
      return {
        value_type: 'binary' as any,
        direction: 'increase_is_good' as any,
        deadline_date: c.deadline_date || undefined,
        tracking_start_date: c.tracking_start_date || undefined,
        // Binary keeps deadline windows in its form (they ARE its only thresholds —
        // its discrete score values map directly to color bands).
        warning_days: c.warning_days ?? 30,
        critical_days: c.critical_days ?? 7,
      };
    }

    // Important: SubKpiInstance has `tracking_mode`, NOT `aggregation`.
    // Earlier versions wrote `aggregation` here, which is invented at runtime
    // via the `as any` cast and never serializes — App.tsx then derived the
    // engine's aggregation from `tracking_mode` (which was undefined), so
    // every Count was silently treated as 'periodic'. Two visible failures
    // resulted: (1) on edit, inferTypeKeyFromLegacy fell through to
    // 'improvement' instead of 'count' because aggregation was missing;
    // (2) the engine used score-delta trend instead of raw-value-delta, so
    // a count going down never showed a down arrow. Writing tracking_mode
    // fixes both. (DEV-814)
    // Helper that lifts the user's color band thresholds onto every type's
    // legacy field output. Without this, green_threshold/yellow_threshold
    // captured by the form vanished on save (DEV-814 — Skip's report).
    const colorBands = {
      green_threshold: Number.isFinite(c.green_threshold) ? (c.green_threshold as number) : undefined,
      yellow_threshold: Number.isFinite(c.yellow_threshold) ? (c.yellow_threshold as number) : undefined,
    };

    // tracking_start_date — the form-level "when scoring should begin"
    // affordance. Backend has the column already (kpi_config.tracking_start_date).
    const trackingStart = c.tracking_start_date || undefined;

    // color_mode — per-instance choice of how the indicator color is
    // derived (DEV-814 / migration 122; 'pace' added DEV-815 / migration 123).
    const colorMode = (c.color_mode === 'score' || c.color_mode === 'deadline' || c.color_mode === 'pace')
      ? (c.color_mode as 'score' | 'deadline' | 'pace')
      : undefined;

    // Pace-mode tolerances (DEV-815). Only meaningful when color_mode='pace'
    // but persist always so toggling modes doesn't lose values.
    const paceTolerances = {
      pace_yellow_units_behind: Number.isFinite(c.pace_yellow_units_behind)
        ? (c.pace_yellow_units_behind as number)
        : undefined,
      pace_red_units_behind: Number.isFinite(c.pace_red_units_behind)
        ? (c.pace_red_units_behind as number)
        : undefined,
    };

    if (typeKey === 'count') {
      return {
        value_type: 'numeric' as any,
        direction: 'increase_is_good' as any,
        tracking_mode: 'cumulative' as any,
        target_goal: Number.isFinite(c.target_value) ? (c.target_value as number) : undefined,
        uom: c.unit_of_measure || undefined,
        deadline_date: c.deadline_date || undefined,
        tracking_start_date: trackingStart,
        color_mode: colorMode,
        ...colorBands,
        // DEV-815: pace-mode tolerances persist regardless of current mode so
        // the user doesn't lose the values when toggling modes.
        ...paceTolerances,
        // warning_days/critical_days are form-visible only when color_mode='deadline'
        // (DEV-814). When the user is in deadline mode they may have customized
        // these; in other modes the engine still uses internal defaults to drive
        // the score-cap behavior in 'score' mode. The deprecated
        // deadline_degradation_enabled toggle is gone — see DEV-814.
        warning_days: Number.isFinite(c.warning_days) ? (c.warning_days as number) : 30,
        critical_days: Number.isFinite(c.critical_days) ? (c.critical_days as number) : 7,
      };
    }

    if (typeKey === 'reduction') {
      return {
        value_type: 'numeric' as any,
        direction: 'decrease_is_good' as any,
        tracking_mode: 'periodic' as any,
        target_goal: Number.isFinite(c.target_value) ? (c.target_value as number) : undefined,
        target_baseline: Number.isFinite(c.baseline_value) ? (c.baseline_value as number) : undefined,
        uom: c.unit_of_measure || undefined,
        deadline_date: c.deadline_date || undefined,
        tracking_start_date: trackingStart,
        color_mode: colorMode,
        ...colorBands,
        // DEV-864/DEV-851 fix: Honor user-configured warning_days / critical_days
        // from the Tolerances step. Was previously hardcoded to 30/7 regardless
        // of user input — Skip's report on Rate KPI: "yellow threshold of 7 days
        // but it's turning yellow at 23 days out." The hardcoded 30 was being
        // used because user input never reached the engine.
        warning_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.warning_days) ? (c.warning_days as number) : 30),
        critical_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.critical_days) ? (c.critical_days as number) : 7),
      };
    }

    if (typeKey === 'improvement') {
      return {
        value_type: 'numeric' as any,
        direction: 'increase_is_good' as any,
        tracking_mode: 'periodic' as any,
        target_goal: Number.isFinite(c.target_value) ? (c.target_value as number) : undefined,
        target_baseline: Number.isFinite(c.baseline_value) ? (c.baseline_value as number) : undefined,
        uom: c.unit_of_measure || undefined,
        deadline_date: c.deadline_date || undefined,
        tracking_start_date: trackingStart,
        color_mode: colorMode,
        ...colorBands,
        // DEV-864/DEV-851 fix: Honor user-configured warning_days / critical_days
        // from the Tolerances step. Was previously hardcoded to 30/7 regardless
        // of user input — Skip's report on Rate KPI: "yellow threshold of 7 days
        // but it's turning yellow at 23 days out." The hardcoded 30 was being
        // used because user input never reached the engine.
        warning_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.warning_days) ? (c.warning_days as number) : 30),
        critical_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.critical_days) ? (c.critical_days as number) : 7),
      };
    }

    if (typeKey === 'rate') {
      return {
        value_type: 'percentage' as any,
        direction: 'increase_is_good' as any,
        target_goal: Number.isFinite(c.target_rate) ? (c.target_rate as number) : undefined,
        uom: '%',
        deadline_date: c.deadline_date || undefined,
        tracking_start_date: trackingStart,
        color_mode: colorMode,
        ...colorBands,
        // DEV-864/DEV-851 fix: Honor user-configured warning_days / critical_days
        // from the Tolerances step. Was previously hardcoded to 30/7 regardless
        // of user input — Skip's report on Rate KPI: "yellow threshold of 7 days
        // but it's turning yellow at 23 days out." The hardcoded 30 was being
        // used because user input never reached the engine.
        warning_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.warning_days) ? (c.warning_days as number) : 30),
        critical_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.critical_days) ? (c.critical_days as number) : 7),
      };
    }

    if (typeKey === 'milestone_set') {
      const total = Array.isArray(c.milestones) ? c.milestones.length : 0;
      return {
        value_type: 'milestone_set' as any,
        direction: 'increase_is_good' as any,
        tracking_mode: 'cumulative' as any, // milestones accumulate over time
        milestone_total: total,
        target_goal: total,
        deadline_date: c.deadline_date || undefined,
        tracking_start_date: trackingStart,
        color_mode: colorMode,
        ...colorBands,
        // DEV-864/DEV-851 fix: Honor user-configured warning_days / critical_days
        // from the Tolerances step. Was previously hardcoded to 30/7 regardless
        // of user input — Skip's report on Rate KPI: "yellow threshold of 7 days
        // but it's turning yellow at 23 days out." The hardcoded 30 was being
        // used because user input never reached the engine.
        warning_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.warning_days) ? (c.warning_days as number) : 30),
        critical_days: c.deadline_degradation_enabled === false
          ? undefined
          : (Number.isFinite(c.critical_days) ? (c.critical_days as number) : 7),
      };
    }

    // Unknown type: return empty — engine will treat as tracked-only.
    return {};
  }

  const isLinearFlow = mode === 'add';
  const currentStepNum = state.showAll ? null : state.currentStep;

  return (
    <div className={styles.wizardContainer}>
      <div className={styles.wizardHeader}>
        <h2 className={styles.wizardTitle}>
          {mode === 'edit'
            ? t('kpi.editSubKpi', 'Edit Sub-KPI')
            : t('kpi.addSubKpi', 'Add Sub-KPI')}
        </h2>
        <button className={styles.closeBtn} onClick={onCancel} aria-label="Close">
          ×
        </button>
      </div>

      {mode === 'edit' && (
        <div className={styles.editBanner}>
          <span>{t('kpi.editModeDesc', 'Editing: Click any step to jump directly to it. Save is always available.')}</span>
          <label className={styles.showAllToggle}>
            <input
              type="checkbox"
              checked={state.showAll}
              onChange={(e) => dispatch({ type: 'TOGGLE_SHOW_ALL' })}
            />
            <span>{t('kpi.showAllSections', 'Show all sections')}</span>
          </label>
        </div>
      )}

      {/* Stepper.
          DEV-822 / Skip: HSE-typed sub-KPIs (incident_count) in edit mode
          collapse the wizard to just Configuration + Reporting. The Type
          step is irrelevant (type is fixed for HSE; can't be changed) and
          the Review step is overkill for low-stakes edits like changing
          display name or period. */}
      {(() => {
        const isHseEdit =
          mode === 'edit' && state.missionTypeKey === 'incident_count';
        const visibleSteps = isHseEdit ? [2, 3] : [1, 2, 3, 4];
        return (
      <div className={styles.stepper}>
        <div className={styles.steps}>
          {visibleSteps.map((stepNum, idx) => (
            <React.Fragment key={stepNum}>
              <div
                className={`${styles.step} ${
                  isLinearFlow
                    ? stepNum < currentStepNum! ? styles.done : stepNum === currentStepNum ? styles.active : ''
                    : (state.showAll
                        ? styles.done                                           /* Show-all view: every section visible, no current step */
                        : stepNum === currentStepNum
                          ? styles.active                                       /* Edit mode: current step = green (in progress) */
                          : styles.done)                                        /* Edit mode: other steps = blue (previously configured) */
                } ${mode === 'edit' && !state.showAll ? styles.clickable : ''}`}
                onClick={() => handleStepClick(stepNum)}
              >
                <div className={styles.stepNum}>{stepNum}</div>
                <div className={styles.stepLabel}>
                  {stepNum === 1 && t('kpi.stepType', 'Type')}
                  {stepNum === 2 && t('kpi.stepConfig', 'Configuration')}
                  {stepNum === 3 && t('kpi.stepReporting', 'Reporting')}
                  {stepNum === 4 && t('kpi.stepReview', 'Review')}
                </div>
              </div>
              {idx < visibleSteps.length - 1 && <div className={styles.stepConn} />}
            </React.Fragment>
          ))}
        </div>
      </div>
        );
      })()}

      {/* Body */}
      <div className={styles.modalBody}>
        {/* Step 1: Type. DEV-882 / Skip — name moved to Step 2 next to the
            rest of the per-type configuration so there is a single Display
            Name field, not two. Step 1 is now a focused type-picker. */}
        {(state.showAll || currentStepNum === 1) && !isHseEdit && (
          <div className={styles.stepContent} data-step="1">
            <h3 className={styles.stepTitle}>{t('kpi.stepType', 'Type')}</h3>
            <p className={styles.stepDesc}>
              {t(
                'kpi.step1DescType',
                'Choose what kind of measurement this sub-KPI tracks. You can name it on the next step.',
              )}
            </p>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t('kpi.typeOfMeasurement', 'Type of measurement')}</label>
              {mode === 'edit' && (
                <div className={styles.typeLockNotice}>
                  {t('kpi.typeCannotChange', 'Type cannot be changed after creation.')}
                </div>
              )}
              <div className={`${styles.typeGrid} ${mode === 'edit' ? styles.typesLocked : ''}`}>
                {availableTypes.map((type) => {
                  // DEV-816 / Skip: expanded type cards. The richer metadata
                  // (examples, use_when framing question, supported color
                  // modes) helps users pick confidently on first encounter.
                  // All fields are optional — types without them render the
                  // legacy display_name + description only.
                  const supportedModes =
                    type.supported_color_modes ?? ['score', 'deadline'];
                  return (
                    <div
                      key={type.type_key}
                      className={`${styles.typeCard} ${state.missionTypeKey === type.type_key ? styles.active : ''}`}
                      onClick={() => {
                        if (mode !== 'edit') {
                          dispatch({ type: 'SET_MISSION_TYPE', value: type.type_key, typeInstance: type });
                        }
                      }}
                    >
                      <div className={styles.typeCardContent}>
                        <div className={styles.typeName}>{type.display_name}</div>
                        <div className={styles.typeDesc}>{type.description}</div>
                        {type.examples && type.examples.length > 0 && (
                          <div className={styles.typeMeta}>
                            <strong>{t('kpi.examples', 'Examples:')}</strong>{' '}
                            {type.examples.join(', ')}
                          </div>
                        )}
                        {type.use_when && (
                          <div className={styles.typeMeta}>
                            <strong>{t('kpi.useWhen', 'Use when:')}</strong>{' '}
                            {type.use_when}
                          </div>
                        )}
                        <div className={styles.typeModes}>
                          {supportedModes.map((m) => (
                            <span key={m} className={styles.typeModeChip}>
                              {m === 'score'    ? t('kpi.modeProgress', 'Progress')
                               : m === 'deadline' ? t('kpi.modeDeadline', 'Deadline')
                               : m === 'pace'    ? t('kpi.modePace', 'Pace')
                               : m}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Configuration */}
        {/* DEV-816 H layout (Skip): Step 2 uses a two-column body when the
            container is wide enough — form on the left, sticky live
            preview on the right. Below the container-query breakpoint
            the layout collapses to single column with the preview pinned
            at the top. Steps 1, 3, 4 stay single-column (the preview
            doesn't earn its keep there). */}
        {(state.showAll || currentStepNum === 2) && selectedType && (
          <div className={styles.stepContent} data-step="2">
            <h3 className={styles.stepTitle}>{selectedType.display_name} {t('kpi.configuration', 'Configuration')}</h3>
            <p className={styles.stepDesc}>
              {t('kpi.step2Desc', 'Configure the measurement parameters, targets, and tolerances.')}
            </p>

            <div className={styles.step2TwoCol}>
              <div className={styles.step2Form}>
                {/* Display Name — moved here from Step 1 (DEV-882 / Skip) so
                    there is a single canonical name field. Mirrored into
                    typeConfig.display_name on every keystroke so per-type
                    validators (which read from typeConfig) stay in sync,
                    and the schema's display_name field is filtered out of
                    the renderer below to avoid a duplicate input. */}
                {(() => {
                  // HSE rows have permanently fixed names (Health, Safety,
                  // Environment) — DEV-822 lock. Render the input as
                  // readOnly with a small explanatory hint so the user
                  // understands why it's not editable.
                  const isHse = state.missionTypeKey === 'incident_count';
                  return (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        {t('common.displayName', 'Display Name')}
                      </label>
                      <input
                        type="text"
                        className={styles.input}
                        value={state.displayName}
                        readOnly={isHse}
                        disabled={isHse}
                        onChange={(e) => {
                          if (isHse) return; // defense in depth — input is also readOnly
                          const value = e.target.value;
                          dispatch({ type: 'SET_DISPLAY_NAME', value });
                          dispatch({
                            type: 'UPDATE_TYPE_CONFIG_FIELD',
                            key: 'display_name',
                            value,
                          });
                        }}
                        placeholder={t(
                          'kpi.displayNamePlaceholder',
                          'e.g., Students Completed',
                        )}
                      />
                      {isHse && (
                        <div className={styles.help}>
                          {t(
                            'kpi.displayNameHseLocked',
                            'HSE card names are platform-fixed and cannot be changed.',
                          )}
                        </div>
                      )}
                      {(displayErrors.displayName || displayErrors.display_name) && (
                        <div className={styles.error}>
                          {displayErrors.displayName || displayErrors.display_name}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Stale-Pace notice. The Pace card itself is disabled in
                    the ModeCardList when the predicate is false, but a
                    previously-saved instance can load with color_mode='pace'
                    AND missing dates. Without this notice the Pace card
                    looks selected-but-inert with no explanation. */}
                {(() => {
                  const cfg = state.typeConfig as any;
                  if (cfg.color_mode !== 'pace') return null;
                  const missingStart = !cfg.tracking_start_date;
                  const missingEnd = !cfg.deadline_date;
                  if (!missingStart && !missingEnd) return null;
                  const which =
                    missingStart && missingEnd
                      ? t('kpi.paceStaleBoth', 'Tracking start date and Target deadline')
                      : missingStart
                      ? t('kpi.paceStaleStart', 'Tracking start date')
                      : t('kpi.paceStaleEnd', 'Target deadline');
                  return (
                    <div className={styles.typeLockNotice} role="alert">
                      <strong>{t('kpi.paceStaleHeading', 'Pace mode needs attention.')}</strong>{' '}
                      {t(
                        'kpi.paceStaleBody',
                        '{{which}} is not set, so Pace coloring cannot be calculated. Restore the missing field below, or pick a different color mode.',
                        { which },
                      )}
                    </div>
                  );
                })()}

                {/* Step-level validation banner — catches cross-field errors
                    that don't have a clean field to attach to. Field-
                    targeted errors render inline at their input. */}
                {displayErrors._type && (
                  <div
                    role="alert"
                    style={{
                      marginBlockEnd: 12,
                      padding: '8px 12px',
                      background: 'color-mix(in srgb, var(--color-error) 12%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--color-error) 40%, transparent)',
                      borderRadius: 6,
                      color: 'var(--color-error)',
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>{t('kpi.validationError', 'Configuration error.')}</strong>{' '}
                    {displayErrors._type}
                  </div>
                )}

                {/* DEV-822: when editing an HSE-typed sub-KPI, hide
                    hse_category from the editable form. Allowing the
                    user to change a Health card to Safety would break
                    the 1-per-category invariant set by default
                    provisioning (project would end up with two Safety
                    cards and zero Health cards, or worse). The current
                    category is shown as a read-only "Category: Health"
                    line above the form so it remains visible. */}
                {(() => {
                  const isHseEdit =
                    mode === 'edit' &&
                    state.missionTypeKey === 'incident_count';
                  // DEV-882 / Skip: drop display_name from the schema-driven
                  // renderer; it's now handled by the single manual input
                  // above. Keeping it in the schema for validators and
                  // tests, but never rendering it twice.
                  const renderedSchema = (isHseEdit
                    ? selectedType.config_schema.filter(
                        (f) => f.key !== 'hse_category',
                      )
                    : selectedType.config_schema
                  ).filter((f) => f.key !== 'display_name');

                  return (
                    <>
                      {/* HSE category display block removed (Skip 2026-05-11).
                          Once the Display Name is locked to its canonical
                          value (Health / Safety / Environment), the category
                          is already conveyed by the name. A separate read-
                          only "Category: Health" line below adds visual
                          clutter without surfacing new information.
                          hse_category is still filtered out of the schema
                          renderer above so the user never sees a dropdown
                          for it either. */}

                      <ConfigFieldRenderer
                        schema={renderedSchema}
                        values={state.typeConfig}
                        onChange={(key, value) => {
                          if (key === 'color_mode' && typeof value === 'string') {
                            dispatch({
                              type: 'CHANGE_COLOR_MODE',
                              value,
                              schema: selectedType.config_schema,
                            });
                          } else {
                            dispatch({ type: 'UPDATE_TYPE_CONFIG_FIELD', key, value });
                          }
                        }}
                        errors={displayErrors}
                      />
                    </>
                  );
                })()}
              </div>

              {/* Sticky live-preview pane on the right. Shows how the
                  indicator will color/score with the current config. The
                  preview re-runs on every typeConfig change so threshold
                  edits show their impact without leaving the form. */}
              <aside className={styles.step2Preview} aria-label={t('kpi.livePreview', 'Live preview')}>
                <LiveScorePreview
                  typeKey={state.missionTypeKey}
                  typeConfig={state.typeConfig}
                  displayName={state.displayName}
                />
              </aside>
            </div>
          </div>
        )}

        {/* Step 3: Reporting.
            DEV-822 + DEV-651: Three Mission types don't have a recurring
            reporting cadence and are skipped by Data Reliability scoring
            (cadence='once' is the engine signal):
              - 'incident_count' (HSE) — event-driven; incidents happen
                when they happen
              - 'binary' — single yes/no outcome; one event, not a stream
              - 'milestone_set' (checklist) — items completed when items
                completed; the checklist itself has a deadline, not a
                cadence
            For all three, hide the cadence widget + permissible-days field
            and surface an explanatory note. Role assignment still shows —
            someone may still be responsible for entering the outcome. */}
        {(state.showAll || currentStepNum === 3) && (() => {
          const isHseType = state.missionTypeKey === 'incident_count';
          const isEventDrivenType =
            isHseType
            || state.missionTypeKey === 'binary'
            || state.missionTypeKey === 'milestone_set';
          const eventDrivenExplain =
            isHseType
              ? t(
                  'kpi.step3DescHse',
                  'HSE incidents are event-driven, not cadence-based — there is no scheduled reporting interval. Designate the role responsible for incident reporting if you want.',
                )
              : state.missionTypeKey === 'binary'
                ? t(
                    'kpi.step3DescBinary',
                    'Binary KPIs are one-time outcomes — they don’t have a recurring reporting cadence. Set a deadline in the configuration step instead.',
                  )
                : t(
                    'kpi.step3DescChecklist',
                    'Checklists are completed item by item — they don’t have a recurring reporting cadence. Use the deadline and per-item due dates in the configuration step instead.',
                  );
          return (
            <div className={styles.stepContent} data-step="3">
              <h3 className={styles.stepTitle}>{t('kpi.reporting', 'Reporting')}</h3>
              <p className={styles.stepDesc}>
                {isEventDrivenType
                  ? eventDrivenExplain
                  : t(
                      'kpi.step3Desc',
                      'Set how often this KPI should be updated and which role is responsible.',
                    )}
              </p>

              {!isEventDrivenType && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>{t('kpi.reportingCadence', 'Reporting Cadence')}</label>
                  <ReportingCadenceWidget
                    value={state.cadenceValue}
                    onChange={(cv) => dispatch({ type: 'SET_CADENCE', value: cv })}
                  />
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.label}>{t('kpi.assignedRole', 'Assigned to (role)')}</label>
                <select
                  className={styles.select}
                  value={state.assignedRoleGuid || ''}
                  onChange={(e) => dispatch({ type: 'SET_ASSIGNED_ROLE', value: e.target.value || null })}
                >
                  <option value="">{t('kpi.unassigned', 'Unassigned')}</option>
                  {/* TODO: Load roles from projectRoles API */}
                  <option value="role-data-steward">Data Steward</option>
                  <option value="role-project-lead">Project Lead</option>
                </select>
              </div>

              {!isEventDrivenType && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>{t('kpi.permissibleDaysLate', 'Permissible days late')}</label>
                  <input
                    type="number"
                    className={styles.input}
                    min="0"
                    max="30"
                    value={state.permissibleDaysLate}
                    onChange={(e) => dispatch({ type: 'SET_PERMISSIBLE_DAYS_LATE', value: parseInt(e.target.value, 10) || 0 })}
                  />
                  <div className={styles.help}>
                    {t('kpi.permissibleDaysLateHelp', 'After this many days past the cadence due date, Data Reliability flags the KPI as overdue.')}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Step 4: Review */}
        {(state.showAll || currentStepNum === 4) && !isHseEdit && (
          <div className={styles.stepContent} data-step="4">
            <h3 className={styles.stepTitle}>{t('kpi.reviewAndSave', 'Review and save')}</h3>
            <p className={styles.stepDesc}>
              {t('kpi.step4Desc', 'Confirm the configuration. You can go back to any earlier step to make changes.')}
            </p>

            <ReviewCard label={t('kpi.identity', 'Identity')}>
              <ReviewRow label={t('kpi.displayName', 'Display name')} value={state.displayName || '(not set)'} />
              <ReviewRow label={t('kpi.type', 'Type')} value={selectedType?.display_name || '—'} />
            </ReviewCard>

            <ReviewCard label={t('kpi.configuration', 'Configuration')}>
              {/* DEV-816 / Skip: render each typeConfig field with its
                  schema label (not raw key like "target_value"), skip
                  display_name (already shown in Identity), skip empty
                  values to avoid noise on Review, and format mode-aware
                  values nicely (color_mode → "Pace", units suffix on
                  threshold values, etc.). */}
              {(() => {
                const schema = selectedType?.config_schema ?? [];
                const cfg = state.typeConfig as Record<string, unknown>;
                const SKIP_KEYS = new Set(['display_name']);
                const COLOR_MODE_LABELS: Record<string, string> = {
                  score:    t('kpi.modeProgressLong',  'Progress (% toward target)'),
                  deadline: t('kpi.modeDeadlineLong',  'Time remaining until deadline'),
                  pace:     t('kpi.modePaceLong',      'Pace (variance from expected)'),
                };
                const rows: Array<{ key: string; label: string; display: string }> = [];
                for (const field of schema) {
                  if (SKIP_KEYS.has(field.key)) continue;
                  // Hide fields that aren't currently visible (e.g., the
                  // off-mode threshold fields) — they don't apply to
                  // saved config and would confuse the Review.
                  if (field.visible_when && !field.visible_when(cfg)) continue;
                  const v = cfg[field.key];
                  if (v === undefined || v === null || v === '') continue;
                  // Special-case rendering by field key.
                  let display: string;
                  if (field.key === 'color_mode' && typeof v === 'string') {
                    display = COLOR_MODE_LABELS[v] ?? v;
                  } else if (field.key === 'milestones' && Array.isArray(v)) {
                    display = v.length === 1 ? '1 milestone' : `${v.length} milestones`;
                  } else if (typeof v === 'number' && field.suffix) {
                    display = `${v} ${field.suffix}`;
                  } else if (Array.isArray(v)) {
                    display = `${v.length} item${v.length === 1 ? '' : 's'}`;
                  } else {
                    display = String(v);
                  }
                  rows.push({ key: field.key, label: field.label, display });
                }
                if (rows.length === 0) {
                  return (
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                      {t('kpi.noConfiguration', 'No configuration set yet.')}
                    </div>
                  );
                }
                return rows.map((r) => (
                  <ReviewRow key={r.key} label={r.label} value={r.display} />
                ));
              })()}
            </ReviewCard>

            <ReviewCard label={t('kpi.reporting', 'Reporting')}>
              {(() => {
                // Format cadence with title case; map role guid to
                // human-readable role name (matches the Step 3 dropdown
                // values until the projectRoles API is wired up).
                const ROLE_LABELS: Record<string, string> = {
                  'role-data-steward': t('kpi.roleDataSteward', 'Data Steward'),
                  'role-project-lead': t('kpi.roleProjectLead', 'Project Lead'),
                };
                const cadence = state.cadenceValue.cadence || '';
                const cadenceDisplay = cadence
                  ? cadence.charAt(0).toUpperCase() + cadence.slice(1)
                  : '—';
                const role = state.assignedRoleGuid;
                const roleDisplay = role
                  ? ROLE_LABELS[role] ?? role
                  : t('kpi.unassigned', 'Unassigned');
                const lateValue = Number.isFinite(state.permissibleDaysLate)
                  ? `${state.permissibleDaysLate} ${state.permissibleDaysLate === 1 ? 'day' : 'days'}`
                  : '—';
                return (
                  <>
                    <ReviewRow label={t('kpi.cadence', 'Cadence')} value={cadenceDisplay} />
                    <ReviewRow label={t('kpi.assignedRole', 'Assigned to (role)')} value={roleDisplay} />
                    <ReviewRow label={t('kpi.permissibleDaysLate', 'Permissible days late')} value={lateValue} />
                  </>
                );
              })()}
            </ReviewCard>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div>
          <button
            className={styles.btn}
            onClick={handleBack}
            disabled={mode === 'edit' || state.currentStep === 1}
          >
            ← {t('common.back', 'Back')}
          </button>
        </div>
        <div className={styles.footerRight}>
          <button className={styles.btn} onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </button>
          {mode === 'add' && (
            <button className={styles.btnPrimary} onClick={handleNext}>
              {state.currentStep === lastStep ? t('common.save', 'Save') : t('common.next', 'Next')} →
            </button>
          )}
          {mode === 'edit' && (
            <button className={styles.btnPrimary} onClick={handleSave}>
              {t('common.save', 'Save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helper Components ──────────────────────────────────────────

interface ConfigFieldRendererProps {
  schema: any[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  errors: Record<string, string>;
}

function ConfigFieldRenderer({ schema, values, onChange, errors }: ConfigFieldRendererProps) {
  const { t } = useAppTranslation('projectConfig');

  if (!schema || schema.length === 0) {
    return null;
  }

  // First pass: filter out hidden fields so the inline_with_next logic
  // operates on the visible sequence (a hidden field shouldn't break a
  // pairing or accidentally pair across a gap). Conditional visibility
  // (DEV-814): types use visible_when to swap score-band thresholds for
  // deadline-window thresholds based on color_mode. A hidden field still
  // keeps its value in typeConfig — we just don't render the input.
  const visibleFields = schema.filter(
    (field) => !field.visible_when || field.visible_when(values),
  );

  // Second pass: group consecutive fields where the previous had
  // `inline_with_next` set. The result is a list of groups: either a
  // single field rendered full-width, or two adjacent fields rendered
  // side-by-side in a 1fr/1fr grid (DEV-816 / Skip's layout feedback —
  // tall column of narrow inputs felt sloppy).
  type Group = { kind: 'solo'; field: any } | { kind: 'pair'; left: any; right: any };
  const groups: Group[] = [];
  for (let i = 0; i < visibleFields.length; i++) {
    const cur = visibleFields[i];
    const next = visibleFields[i + 1];
    if (cur.inline_with_next && next) {
      groups.push({ kind: 'pair', left: cur, right: next });
      i++; // consume the next field too
    } else {
      groups.push({ kind: 'solo', field: cur });
    }
  }

  // Inline field renderer — keeps the per-field JSX in one place so the
  // pair-vs-solo layout doesn't duplicate it.
  const renderField = (field: any) => {
    const value = values[field.key];
    const error = errors[field.key];
    return (
      <div key={field.key} className={styles.formGroup}>
            <label className={styles.label}>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            {field.type === 'string' && (
              <input
                type="text"
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                value={(value as string | undefined) || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
                placeholder={field.help_text}
              />
            )}
            {field.type === 'number' && !field.suffix && (
              <input
                type="number"
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                value={value === 0 || value ? String(value) : ''}
                onChange={(e) => {
                  // parseFloat('') = NaN. Push undefined for empty input so
                  // bridge + validation see a true "missing" rather than NaN
                  // (which is typeof 'number' and would slip past naïve checks).
                  const raw = e.target.value;
                  const parsed = raw === '' ? undefined : parseFloat(raw);
                  onChange(
                    field.key,
                    parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined,
                  );
                }}
                min={field.validation?.min}
                max={field.validation?.max}
              />
            )}
            {field.type === 'number' && field.suffix && (
              // DEV-816 / Skip: number input with a unit suffix bolted to
              // the trailing edge. The suffix swaps with the active mode
              // (% / days / units) so the user never has to guess what
              // unit the typed number represents. Wrapper takes the error
              // border styling so it visually matches plain inputs.
              <div className={`${styles.inputWithSuffix} ${error ? styles.inputError : ''}`}>
                <input
                  type="number"
                  className={styles.inputBare}
                  value={value === 0 || value ? String(value) : ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const parsed = raw === '' ? undefined : parseFloat(raw);
                    onChange(
                      field.key,
                      parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined,
                    );
                  }}
                  min={field.validation?.min}
                  max={field.validation?.max}
                />
                <div className={styles.inputSuffix}>{field.suffix}</div>
              </div>
            )}
            {field.type === 'date' && (
              <input
                type="date"
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                value={(value as string | undefined) || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
              />
            )}
            {field.type === 'enum' && field.key === 'color_mode' && (
              // DEV-816: color_mode is the most consequential choice in the
              // form. Render as visual card rows (ModeCardList) instead of
              // a dropdown so it gets the visual weight it deserves and the
              // user sees per-mode help inline rather than after-the-click.
              //
              // Per-option `enabled_when` (DEV-816 / Skip): some modes require
              // sibling fields to be populated first (e.g. Pace needs both
              // tracking_start_date AND deadline_date). Cards stay visible
              // but render disabled so the user sees the option exists and
              // what to do to unlock it. If the user had Pace selected and
              // then cleared a date, we DON'T silently switch modes — the
              // selected card stays selected even when disabled, and validation
              // surfaces the missing prerequisite at save time.
              <ModeCardList
                ariaLabel={field.label}
                value={value as string | undefined}
                onChange={(v) => onChange(field.key, v)}
                options={(field.enum_options ?? []).map((opt: any): ModeOption => {
                  const isEnabled = opt.enabled_when ? opt.enabled_when(values) : true;
                  return {
                    value: opt.value,
                    label: opt.label,
                    help: opt.help ?? '',
                    sparkline: renderModeSparkline(opt.value),
                    disabled: !isEnabled,
                    disabledReason: !isEnabled ? opt.disabled_reason : undefined,
                  };
                })}
              />
            )}
            {field.type === 'enum' && field.key !== 'color_mode' && (
              <select
                className={`${styles.select} ${error ? styles.inputError : ''}`}
                value={(value as string | undefined) || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
              >
                <option value="">(select)</option>
                {field.enum_options?.map((opt: any) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {/* DEV-816 / Skip's bug report: the 'list' field type (used by
                Milestone Set for the milestones[] array) had no renderer
                and silently dropped through. Users couldn't enter any
                milestones — making the type unusable. This editor lets
                them add, edit, and remove items; IDs auto-generate so
                rows survive reordering and label edits without losing
                continuity in saved data. */}
            {field.type === 'list' && (
              <MilestoneListEditor
                value={Array.isArray(value) ? (value as Array<{ id: string; label: string }>) : []}
                onChange={(items) => onChange(field.key, items)}
              />
            )}
            {error && <div className={styles.error}>{error}</div>}
            {/*
              Help text precedence (DEV-816 / Skip — eliminate duplication):
                - color_mode renders as ModeCardList (cards). Each card has
                  its own help text inside. Showing the selected option's
                  help below the picker would duplicate what the user is
                  already reading inside the selected card. Suppress here.
                - Other enum fields render as <select>. The user has no way
                  to see the selected option's help without expanding the
                  dropdown. Show the help below.
                - Non-enum fields show static field.help_text below.
              Hidden when an error is shown so the user sees the error first.
            */}
            {!error && field.type === 'enum' && field.key !== 'color_mode' && (() => {
              const selectedOpt = (field.enum_options ?? []).find(
                (o: any) => o.value === value,
              ) as any;
              if (selectedOpt?.help) {
                return <div className={styles.help}>{selectedOpt.help}</div>;
              }
              return field.help_text ? <div className={styles.help}>{field.help_text}</div> : null;
            })()}
            {!error && field.type !== 'enum' && field.help_text && (
              <div className={styles.help}>{field.help_text}</div>
            )}
          </div>
        );
  };

  return (
    <div className={styles.configFieldsContainer}>
      {groups.map((g) => {
        if (g.kind === 'pair') {
          return (
            <div
              key={`${g.left.key}__${g.right.key}`}
              className={styles.fieldPair}
            >
              {renderField(g.left)}
              {renderField(g.right)}
            </div>
          );
        }
        return renderField(g.field);
      })}
    </div>
  );
}

interface ReviewCardProps {
  label: string;
  children: React.ReactNode;
}

function ReviewCard({ label, children }: ReviewCardProps) {
  return (
    <div className={styles.reviewCard}>
      <h4 className={styles.reviewCardTitle}>{label}</h4>
      {children}
    </div>
  );
}

interface ReviewRowProps {
  label: string;
  value: string;
}

function ReviewRow({ label, value }: ReviewRowProps) {
  return (
    <div className={styles.reviewRow}>
      <span className={styles.reviewLabel}>{label}</span>
      <span className={styles.reviewValue}>{value}</span>
    </div>
  );
}

// ── Milestone list editor (DEV-816 / Skip — bug fix) ───────────
//
// Renders the `milestones[]` field for the Milestone Set type. Without
// this, the field's `type: 'list'` fell through ConfigFieldRenderer's
// switch with no UI, and users had no way to enter milestone names. The
// type was effectively unusable.
//
// Each row shows a 1-based index, a single text input for the milestone
// label, and a delete button. "+ Add milestone" appends an empty row.
// IDs auto-generate so saved data doesn't lose continuity if the user
// reorders or edits labels later.

interface MilestoneListEditorProps {
  value: Array<{ id: string; label: string }>;
  onChange: (items: Array<{ id: string; label: string }>) => void;
}

function MilestoneListEditor({ value, onChange }: MilestoneListEditorProps) {
  const { t } = useAppTranslation('projectConfig');
  const items = value || [];

  const handleAdd = () => {
    // Short readable ID; collisions are vanishingly unlikely within a
    // single milestone set and can't break the engine because IDs are
    // only used for stable React keys + display ordering.
    const newId = `m-${Math.random().toString(36).slice(2, 8)}`;
    onChange([...items, { id: newId, label: '' }]);
  };

  const handleEditLabel = (idx: number, label: string) => {
    onChange(items.map((m, i) => (i === idx ? { ...m, label } : m)));
  };

  const handleDelete = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div className={styles.milestoneList}>
      {items.length === 0 && (
        <div className={styles.milestoneEmpty}>
          {t('kpi.milestonesEmpty', 'No milestones yet. Click "Add milestone" to add the first item.')}
        </div>
      )}
      {items.map((m, i) => (
        <div key={m.id} className={styles.milestoneRow}>
          <span className={styles.milestoneIndex}>{i + 1}.</span>
          <input
            type="text"
            className={styles.milestoneInput}
            value={m.label}
            onChange={(e) => handleEditLabel(i, e.target.value)}
            placeholder={t('kpi.milestonePlaceholder', 'Milestone description')}
            aria-label={t('kpi.milestoneAriaLabel', 'Milestone {{n}}', { n: i + 1 })}
          />
          <button
            type="button"
            className={styles.milestoneDelete}
            onClick={() => handleDelete(i)}
            aria-label={t('kpi.milestoneDelete', 'Delete milestone {{n}}', { n: i + 1 })}
            title={t('kpi.milestoneDelete', 'Delete milestone {{n}}', { n: i + 1 })}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className={styles.milestoneAdd} onClick={handleAdd}>
        + {t('kpi.milestoneAdd', 'Add milestone')}
      </button>
    </div>
  );
}

// ── Mode sparklines (DEV-816 / Variant E) ──────────────────────
//
// Tiny inline SVGs hinting at each color_mode's behavior. Rendered into
// the ModeCardList option rows. Stylistic, not data-driven — they don't
// reflect the user's actual KPI, just the conceptual shape of the mode.

function renderModeSparkline(mode: string): React.ReactNode {
  if (mode === 'score') {
    return (
      <svg viewBox="0 0 80 32" preserveAspectRatio="none" width="80" height="32">
        <path d="M0,32 L0,22 L40,14 L80,4 L80,32 Z" fill="rgba(69,187,71,0.18)" />
        <path d="M0,22 L40,14 L80,4" stroke="var(--color-success)" strokeWidth="2" fill="none" />
      </svg>
    );
  }
  if (mode === 'deadline') {
    return (
      <svg viewBox="0 0 80 32" preserveAspectRatio="none" width="80" height="32">
        <rect x="0" y="4" width="50" height="24" fill="rgba(69,187,71,0.2)" />
        <rect x="50" y="4" width="22" height="24" fill="rgba(255,209,58,0.2)" />
        <rect x="72" y="4" width="8" height="24" fill="rgba(213,53,65,0.2)" />
      </svg>
    );
  }
  if (mode === 'pace') {
    return (
      <svg viewBox="0 0 80 32" preserveAspectRatio="none" width="80" height="32">
        <path d="M0,28 L80,4" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeDasharray="2,2" fill="none" />
        <circle cx="20" cy="22" r="3" fill="var(--color-success)" />
        <circle cx="50" cy="16" r="3" fill="var(--color-warning)" />
        <circle cx="68" cy="22" r="3" fill="var(--color-error)" />
      </svg>
    );
  }
  return null;
}

// ── Reverse Bridge (legacy → typeConfig) ───────────────────────
//
// Companion to bridgeToLegacyFields(). Used by createInitialState() in edit
// mode when the canonical mission_type_config is missing or empty (e.g.
// because the backend hasn't shipped the migration to persist it yet, or
// the instance predates the wizard). Reconstructs typeConfig from whatever
// legacy fields are populated so the user sees their saved values.
//
// The reverse bridge is best-effort: legacy fields cover most type-specific
// values (target_goal, target_baseline, uom, deadline_date, warning_days,
// critical_days), but cannot reconstruct values that have no legacy
// equivalent — notably the milestone_set `milestones[]` array, the
// `green_threshold`/`yellow_threshold` overrides, and free-text fields like
// `achievement_definition` / `calculation_description`. Those fields stay
// empty after reverse-bridging, and the user must re-enter them on edit.
//
// Once the API migration ships (mission_type_config column on the backend),
// this function is mostly dead code — kept only to handle truly-legacy rows
// that predate wizard-created instances.

function inferTypeKeyFromLegacy(existing: SubKpiInstance): string | null {
  const valueType = (existing as any).value_type as string | undefined;
  const direction = (existing as any).direction as string | undefined;
  // tracking_mode is the canonical persisted field. Older code accidentally
  // wrote `aggregation` (an ephemeral runtime property never serialized) —
  // we keep it as a fallback for any in-memory instance that hasn't yet
  // round-tripped through the API. (DEV-814)
  const trackingMode =
    ((existing as any).tracking_mode as string | undefined) ??
    ((existing as any).aggregation as string | undefined);

  if (valueType === 'binary') return 'binary';
  if (valueType === 'percentage') return 'rate';
  if (valueType === 'milestone_set') return 'milestone_set';

  if (valueType === 'numeric' || valueType === undefined) {
    if (direction === 'decrease_is_good') return 'reduction';
    // increase_is_good + cumulative ⇒ count (running total toward a target).
    if (direction === 'increase_is_good' && trackingMode === 'cumulative') {
      return 'count';
    }
    // increase_is_good + non-cumulative ⇒ improvement (measured value rising
    // from a baseline toward a higher target).
    if (direction === 'increase_is_good') return 'improvement';
  }

  return null;
}

function reverseBridgeFromLegacy(
  typeKey: string,
  existing: SubKpiInstance,
): Record<string, unknown> {
  const i = existing as any;
  const common = {
    display_name: i.display_name ?? '',
    deadline_date: i.deadline_date ?? undefined,
    tracking_start_date: i.tracking_start_date ?? undefined,
    warning_days: i.warning_days ?? undefined,
    critical_days: i.critical_days ?? undefined,
    // If warning/critical days are explicitly null in storage, the user
    // disabled deadline degradation. Inferred default is true otherwise.
    deadline_degradation_enabled:
      i.warning_days === null && i.critical_days === null ? false : true,
    // Color band thresholds — populated for non-Binary types so the form
    // re-displays the user's saved values on edit.
    green_threshold: typeof i.green_threshold === 'number' ? i.green_threshold : undefined,
    yellow_threshold: typeof i.yellow_threshold === 'number' ? i.yellow_threshold : undefined,
    // Color mode (DEV-814 / migration 122; 'pace' added DEV-815) — per-instance preference.
    color_mode: (i.color_mode === 'score' || i.color_mode === 'deadline' || i.color_mode === 'pace')
      ? i.color_mode
      : undefined,
    // Pace-mode tolerances (DEV-815). Populated for edit so the user sees
    // their saved values when re-opening a pace-mode KPI.
    pace_yellow_units_behind: typeof i.pace_yellow_units_behind === 'number' ? i.pace_yellow_units_behind : undefined,
    pace_red_units_behind: typeof i.pace_red_units_behind === 'number' ? i.pace_red_units_behind : undefined,
  };

  if (typeKey === 'binary') {
    return { ...common, achievement_definition: undefined };
  }

  if (typeKey === 'count') {
    return {
      ...common,
      target_value: typeof i.target_goal === 'number' ? i.target_goal : undefined,
      unit_of_measure: i.uom ?? '',
    };
  }

  if (typeKey === 'reduction') {
    return {
      ...common,
      baseline_value:
        typeof i.target_baseline === 'number' ? i.target_baseline : undefined,
      target_value: typeof i.target_goal === 'number' ? i.target_goal : undefined,
      unit_of_measure: i.uom ?? '',
    };
  }

  if (typeKey === 'improvement') {
    return {
      ...common,
      baseline_value:
        typeof i.target_baseline === 'number' ? i.target_baseline : undefined,
      target_value: typeof i.target_goal === 'number' ? i.target_goal : undefined,
      unit_of_measure: i.uom ?? '',
    };
  }

  if (typeKey === 'rate') {
    return {
      ...common,
      target_rate: typeof i.target_goal === 'number' ? i.target_goal : undefined,
      calculation_description: undefined,
    };
  }

  if (typeKey === 'milestone_set') {
    // The milestones[] array has no legacy equivalent. We can recover the
    // total count from target_goal but not the named items. The user will
    // have to re-enter the milestone list — surfaced empty so it's obvious.
    return {
      ...common,
      milestones: [],
      required_completion_pct: 100,
    };
  }

  return common;
}

// ── Validation ─────────────────────────────────────────────────

function validateStep(
  stepNum: number,
  state: WizardState,
  selectedType: MissionSubKpiType | null,
): Record<string, string> {
  const errors: Record<string, string> = {};

  // Step 1 is now type-only (DEV-882 / Skip). A type is always pre-selected
  // (defaulted from the registry on add, or loaded from the existing
  // instance on edit), so there is nothing to block advancement on.

  if (stepNum === 2 && selectedType) {
    // DEV-882 / Skip: display name is now captured on Step 2 alongside
    // the rest of the configuration. Check it here so the inline error
    // appears next to the manual Display Name input on this step.
    if (!state.displayName.trim()) {
      errors.displayName = 'Display name is required';
    }

    for (const field of selectedType.config_schema) {
      // Skip the schema's display_name entry — handled by the manual input
      // above. The wizard mirrors state.displayName into typeConfig on every
      // keystroke, so the per-type validators (which run on the merged config
      // in the useEffect path) still see the value; this loop just shouldn't
      // re-attach an error to a field that has no rendered input.
      if (field.key === 'display_name') continue;
      // Skip validation for fields that the form is not currently showing
      // (DEV-814 — color_mode swaps which threshold fields are visible).
      // A hidden field's value is irrelevant to the current configuration
      // and shouldn't block save with "is required" or out-of-range errors.
      if (field.visible_when && !field.visible_when(state.typeConfig)) continue;

      const v = state.typeConfig[field.key];
      // Required check — "missing" means undefined, null, or empty string.
      // 0 is a valid value (e.g., baseline) and must NOT be treated as missing.
      const isMissing =
        v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (typeof v === 'number' && !Number.isFinite(v)); // catches NaN
      if (field.required && isMissing) {
        errors[field.key] = `${field.label} is required`;
        continue; // no point in min/max-checking a missing value
      }
      if (field.validation?.min !== undefined && Number.isFinite(v)) {
        const val = v as number;
        if (val < field.validation.min) {
          errors[field.key] = `${field.label} must be at least ${field.validation.min}`;
        }
      }
      if (field.validation?.max !== undefined && Number.isFinite(v)) {
        const val = v as number;
        if (val > field.validation.max) {
          errors[field.key] = `${field.label} must be at most ${field.validation.max}`;
        }
      }
    }
    // Also run the type's own validate_config — catches cross-field rules
    // like "target must be > baseline" that the per-field loop can't see.
    //
    // validate_config can return either:
    //   - a plain string (legacy form — surfaces as a step-level banner via
    //     errors._type so any cross-field error is at least visible), or
    //   - { field, message } — routes the error to a specific schema field
    //     so the wizard renders it inline next to the offending input
    //     (DEV-816 / Skip — needed for threshold-ordering and lifecycle-
    //     date errors to land where the user is looking).
    if (selectedType.validate_config) {
      // Same merge as the real-time validator path — see useEffect above.
      // displayName lives at the wizard's top level and must be folded
      // into typeConfig so per-type validators see the full config shape.
      const configForValidation = {
        ...state.typeConfig,
        display_name: state.displayName,
      };
      const typeError = selectedType.validate_config(configForValidation as any);
      if (typeError) {
        if (typeof typeError === 'string') {
          errors._type = typeError;
        } else {
          // Field-targeted error. If a min/max error already wrote to that
          // field, the cross-field message wins (it's typically more useful
          // because it explains the relationship).
          errors[typeError.field] = typeError.message;
        }
      }
    }
  }

  return errors;
}

// ── Utilities ──────────────────────────────────────────────────

function generateGuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
